// ═══════════════════════════════════════════════════════════════════════════
// Estados de facturación y cobranza — lote 2.5, observación 3
//
// El operador: "Cuentas solo dice totales, y no se distingue entre facturas
// pagadas, emitidas, remisiones generadas. Todo cuenta igual."
//
// Son DOS DIMENSIONES INDEPENDIENTES. Una remisión puede estar pagada y sin
// facturar, o facturada y sin pagar. Colapsarlas en un número es justo lo que
// hacía inútil la pestaña.
//
//   FACTURACIÓN — derivable de los datos que YA existen
//   COBRANZA    — requiere el registro de pagos (dato nuevo)
//
// Los estados NUNCA se almacenan: se derivan. Un estado guardado se
// desactualiza; una suma no. Los pagos son APPEND-ONLY, igual que el
// inventario: corregir = registrar un pago negativo, jamás editar.
// Cada pago migra 1:1 a la tabla `payment` de la Etapa 4 (MODELO-DATOS §5.2).
// ═══════════════════════════════════════════════════════════════════════════

export const FACTURACION = {
  facturada:        { clave: "facturada",        etiqueta: "Facturada",          color: "#4ade80" },
  por_facturar:     { clave: "por_facturar",     etiqueta: "Por facturar",       color: "#facc15" },
  publico_general:  { clave: "publico_general",  etiqueta: "Público en general", color: "#70a0c9" },
  cancelada:        { clave: "cancelada",        etiqueta: "Cancelada",          color: "#c97070" },
};

export const COBRANZA = {
  pendiente: { clave: "pendiente", etiqueta: "Pendiente", color: "#c97070" },
  parcial:   { clave: "parcial",   etiqueta: "Parcial",   color: "#facc15" },
  pagada:    { clave: "pagada",    etiqueta: "Pagada",    color: "#4ade80" },
  // Salió y no se espera pago. El operador: "hay remisiones que nunca se van a
  // pagar, porque fueron cortesías o porque se mandaron para una demostración."
  //
  // Es un estado propio y no "pagada" a propósito: las dos cierran el saldo,
  // pero una entró dinero y la otra no. Llamarlas igual mentiría en los dos
  // sentidos — inflaría lo cobrado y escondería lo que se regaló.
  sin_cargo: { clave: "sin_cargo", etiqueta: "Sin cargo", color: "#a0a0c9" },

  // Entró MÁS de lo que se debía. El operador: "puede ser que el cliente haya
  // duplicado el pago y debemos tener evidencia y poder hacer conciliación."
  //
  // No es un error ni un estado bueno: es un hecho que alguien tiene que
  // resolver —devolver, aplicar a otra remisión, o descubrir que fue un
  // duplicado—. Por eso tiene color propio y llamativo en vez de esconderse
  // dentro de "pagada", que es donde caía antes: `saldoPendiente` recortaba a
  // cero y el excedente desaparecía de la vista sin dejar rastro.
  a_favor:   { clave: "a_favor",   etiqueta: "Saldo a favor", color: "#70c9c9" },
};

// ── Las clases de movimiento, nombradas ───────────────────────────────────
//
// Estaban implícitas en filtros sueltos (`p.kind && p.kind !== "dinero"`) y eso
// se rompió sin ruido al aparecer `aplicacion`: un saldo a favor aplicado a
// otra remisión caía en "todo lo que no es dinero" y se contaba como CORTESÍA.
// La remisión se habría visto "Sin cargo" —regalada— cuando en realidad se
// pagó con dinero que el cliente ya había entregado.
//
// Por eso ahora las clases se enumeran. Una clase nueva que nadie liste no se
// cuela en el grupo equivocado: no cuenta en ningún lado hasta que alguien
// decida dónde va.

/** Cubre la remisión CON DINERO. `aplicacion` lo es: es dinero que ya entró. */
const DINERO = new Set(["dinero", "aplicacion"]);

/** Cubre la remisión SIN dinero. Cierra el saldo pero no es ingreso. */
const CONDONADO = new Set(["cortesia", "demostracion"]);

const esDinero    = (p) => !p.kind || DINERO.has(p.kind);
const esCondonado = (p) => CONDONADO.has(p.kind);

// Un centavo es la unidad indivisible: se redondea CADA importe antes de
// sumarlo, nunca la suma al final. La base hace lo mismo desde la migración
// 0040 — y hace falta que sea el mismo criterio, porque sum(round(x)) no es
// round(sum(x)) y ahí nacían saldos a favor de un centavo, negativos incluidos.
const centavos = (n) => Math.round((Number(n) || 0) * 100);

const sumaCentavos = (pagos) =>
  pagos.reduce((s, p) => s + centavos(p.monto ?? p.amount), 0);

/** Lo que salió sin cobrarse: cortesías y demostraciones. */
export const totalCondonado = (inv) =>
  sumaCentavos((inv.payments || []).filter(esCondonado)) / 100;

/**
 * Estado de FACTURACIÓN. Se deriva sin datos nuevos:
 *   · cancelada        → el CFDI fue cancelado
 *   · facturada        → tiene CFDI vigente
 *   · público general  → el cliente no tiene RFC: se ampara en factura global
 *   · por facturar     → el cliente TIENE RFC pero aún no se timbra
 */
/** ¿Está cancelada? Puede serlo el CFDI o la remisión misma. */
export const remisionCancelada = (inv) =>
  inv?.cfdiStatus === "canceled" || !!inv?.canceledAt;

export function estadoFacturacion(inv) {
  if (remisionCancelada(inv)) return FACTURACION.cancelada;
  if (inv.cfdiId || inv.cfdiUuid) return FACTURACION.facturada;
  const rfc = (inv.client?.rfc || "").trim();
  return rfc ? FACTURACION.por_facturar : FACTURACION.publico_general;
}

/** Suma de los pagos registrados (los negativos son correcciones). */
export function totalPagado(inv) {
  // Solo el dinero. Una cortesía cierra el saldo pero no es cobro: mezclarlas
  // haría que la vinícola creyera que ingresó lo que regaló.
  return sumaCentavos((inv.payments || []).filter(esDinero)) / 100;
}

/**
 * Saldo por cobrar, EN CENTAVOS redondeados.
 *
 * Debe usar la misma unidad que estadoCobranza: con aritmética de punto
 * flotante una remisión podía quedar "Pagada" y a la vez reportar saldo de
 * $0.001, que se imprime como "$0.00". Las dos funciones cuentan centavos.
 */
export function saldoPendiente(inv) {
  // Cierran el saldo TANTO el dinero como lo condonado: en los dos casos ya no
  // hay nada que perseguir, que es para lo único que sirve un saldo.
  const cerrado = centavos(totalPagado(inv)) + centavos(totalCondonado(inv));
  const falta = centavos(inv.total) - cerrado;
  return falta <= 0 ? 0 : falta / 100;
}

/**
 * Lo que entró DE MÁS. Cero si no sobró nada.
 *
 * Va aparte de `saldoPendiente` y no como un número negativo: un saldo por
 * cobrar y un saldo a favor son dos cosas que se persiguen distinto —una se
 * cobra, la otra se devuelve o se aplica— y sumarlas en una sola cifra
 * escondería las dos.
 *
 * Solo cuenta DINERO: condonar de más no genera un favor, genera un error de
 * captura que se corrige revirtiendo.
 */
export function saldoAFavor(inv) {
  const sobra = centavos(totalPagado(inv)) + centavos(totalCondonado(inv))
              - centavos(inv.total);
  return sobra <= 0 ? 0 : sobra / 100;
}

/** Estado de COBRANZA, derivado de la suma de pagos. Nunca se almacena. */
export function estadoCobranza(inv) {
  const dinero = centavos(totalPagado(inv));
  const cerrado = dinero + centavos(totalCondonado(inv));
  const total = centavos(inv.total);
  if (cerrado <= 0) return COBRANZA.pendiente;
  // El excedente se declara ANTES que "pagada": lo que sobra hay que
  // resolverlo, y esconderlo dentro de "pagada" es como no haberlo cobrado.
  if (cerrado > total) return COBRANZA.a_favor;
  if (cerrado >= total) return dinero > 0 ? COBRANZA.pagada : COBRANZA.sin_cargo;
  return COBRANZA.parcial;
}

/**
 * Agrega un pago. Devuelve una remisión NUEVA (no muta) con el pago añadido.
 * El id se genera aquí, al crear: es lo que hace la migración idempotente y
 * evita la clase de duplicado del folio 376.
 */
export function registrarPago(inv, { monto, fecha, metodo, cuenta, nota, id, revierteA,
                                    kind = "dinero", motivo }) {
  const m = Number(monto);
  if (!m || Number.isNaN(m)) throw new Error("El monto del pago debe ser un número distinto de cero.");
  // El motivo lo exige lo CONDONADO, no "todo lo que no sea dinero": aplicar un
  // saldo a favor no necesita justificarse, el dinero ya lo había entregado el
  // cliente. La restricción `payment_cortesia_con_motivo` de la base tenía este
  // mismo error y bloqueaba las aplicaciones; quedan iguales las dos.
  if (CONDONADO.has(kind) && !String(motivo || "").trim())
    throw new Error("Una cortesía necesita motivo: por qué no se va a cobrar.");
  const pago = {
    id: id || `pay_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    monto: m,
    fecha: fecha || new Date().toISOString(),
    metodo: metodo || "",
    // A qué cuenta entró. Una vinícola con varios bancos necesita distinguirlas:
    // "transferencia" no basta para conciliar. En la Etapa 4 es FK a bank_account.
    cuenta: cuenta || "",
    nota: nota || "",
    // Cortesía o demostración: cierra el saldo y NO es cobro. El motivo se
    // exige —también en la base— porque una cortesía sin explicación es una
    // que nadie va a poder justificar en tres meses.
    kind,
    ...(CONDONADO.has(kind) ? { motivo: motivo || "" } : {}),
    // Si este movimiento revierte otro, se deja el rastro (append-only).
    ...(revierteA ? { revierteA } : {}),
  };
  return { ...inv, payments: [...(inv.payments || []), pago] };
}

/** ¿Esta remisión es de las que llevan complemento por cada pago? */
export function esPPD(inv) {
  return (inv.metodoPago || "").toUpperCase().includes("PPD") ||
         /parcialidades|diferido/i.test(inv.client?.paymentTiming || "");
}

/**
 * Los pagos que TODAVÍA deben su complemento.
 *
 * CRITERIO FISCAL RESUELTO. Una `aplicacion` —saldo a favor que se pasa a esta
 * factura— cuenta como pago recibido y por tanto pide complemento. El REP
 * relaciona un PAGO con una FACTURA, y esta factura quedó pagada en la fecha en
 * que se aplicó, aunque el dinero hubiera entrado antes por otra.
 *
 * Estuvo un tiempo anotado como provisional, elegido por ser el lado
 * conservador del error: emitir un complemento de más se corrige cancelándolo;
 * no emitir el que tocaba es una omisión ante el SAT con plazo vencido.
 *
 * El operador lo confirmó dos veces, y la segunda cerró el punto:
 *   "Si una factura PPD se salda aplicando saldo a favor se debe emitir un
 *    complemento de pago. Se ofrece emitirlo. El dinero ya entró a la vinícola
 *    y hay que aplicarlo a alguna factura."
 *   "Se emite REP al aplicar saldo a favor."
 *
 * Ya no es una suposición a la espera de que alguien la revise.
 */
export function pagosSinREP(inv) {
  if (!esPPD(inv) || !(inv.cfdiId || inv.cfdiUuid)) return [];
  // Una factura cancelada no debe complementos. Sin esto, cancelar factura y
  // complemento —que es lo que manda la regla— dejaría la remisión pidiendo a
  // gritos un REP para un comprobante que ya no existe.
  if (remisionCancelada(inv)) return [];
  return (inv.payments || []).filter(p =>
    // Solo el dinero: una cortesía no se le declara al SAT como pago recibido.
    esDinero(p) &&
    Number(p.monto) > 0 &&
    !p.revierteA &&                 // una reversa no ampara nada
    // Ni lo que YA se revirtió. Un cheque devuelto no es un pago recibido, así
    // que no hay nada que declararle al SAT. Sin esto, revertir un pago en una
    // PPD dejaba la remisión pidiendo el complemento de un pago deshecho —
    // salió a la luz al escribir la regla de la reversa, pero ya estaba.
    !estaRevertido(inv, p.id) &&
    // Lo que ya tiene el suyo, ya está — salvo que ese complemento se haya
    // CANCELADO: entonces el pago vuelve a deberlo, porque ante el SAT es como
    // si nunca se hubiera declarado. Cancelar un REP sin reemitirlo deja una
    // factura PPD cobrada y sin amparar.
    !(p.rep?.facturapiId && p.rep.status !== "cancelado"));
}

/**
 * ⚠ Complemento de pago (REP): si la factura es PPD, cada pago recibido obliga
 * a emitir un Recibo Electrónico de Pagos ante el SAT.
 *
 * DEVUELVE FALSE CUANDO YA NO FALTA NINGUNO. Antes contestaba que sí mientras
 * hubiera pagos, emitidos o no, así que después de emitir el complemento la
 * pantalla seguía avisando "el sistema aún no lo emite" — sobre algo que
 * acababa de emitir. El aviso se volvía decorado y dejaba de leerse.
 */
export function requiereREP(inv) {
  return pagosSinREP(inv).length > 0;
}

/**
 * Los complementos VIVOS de esta remisión: los REP emitidos y no cancelados.
 *
 * El operador: "para cancelar una factura timbrada que además tiene complemento
 * de pago emitido, se cancelan ambos."
 *
 * Y en ese orden. Un REP declara ante el SAT el pago de una factura concreta;
 * cancelar la factura dejando vivo el complemento deja un comprobante que
 * ampara el pago de algo que ya no existe. Por eso esto no es un detalle de
 * presentación: es lo que hay que cancelar ANTES.
 *
 * La misma regla está en el servidor (función `timbrar`, acción `cancelar`),
 * escrita en los dos lados a propósito: aquí para poder decirlo antes de
 * empezar, allá para que se cumpla aunque nadie pase por esta pantalla.
 */
export function complementosVivos(inv) {
  return (inv?.payments || [])
    .filter(p => p.rep?.facturapiId && p.rep.status !== "cancelado")
    .map(p => ({ pagoId: p.id, fecha: p.fecha, monto: Number(p.monto) || 0,
                 facturapiId: p.rep.facturapiId, uuid: p.rep.uuid }));
}

/** El complemento vivo de UN pago, si lo tiene. */
export const complementoVivoDe = (inv, pagoId) =>
  complementosVivos(inv).find(r => r.pagoId === pagoId) || null;

/** Resumen de una lista de remisiones, por las dos dimensiones. */
export function resumenCartera(invoices) {
  const r = {
    total: 0, pagado: 0, porCobrar: 0,
    facturacion: {}, cobranza: {}, repPendientes: 0, cantidad: invoices.length,
  };
  for (const clave of Object.keys(FACTURACION)) r.facturacion[clave] = { n: 0, importe: 0 };
  for (const clave of Object.keys(COBRANZA))    r.cobranza[clave]    = { n: 0, importe: 0 };

  for (const inv of invoices) {
    const monto = Number(inv.total) || 0;
    const cancelada = remisionCancelada(inv);
    const f = estadoFacturacion(inv);
    r.facturacion[f.clave].n++;
    r.facturacion[f.clave].importe += monto;

    // Las canceladas no cuentan para cobranza: no hay nada que cobrar.
    if (!cancelada) {
      const c = estadoCobranza(inv);
      r.cobranza[c.clave].n++;
      r.cobranza[c.clave].importe += monto;
      r.total += monto;
      r.pagado += totalPagado(inv);
      r.porCobrar += saldoPendiente(inv);
      if (requiereREP(inv)) r.repPendientes++;
    }
  }
  return r;
}

/** Cuentas usadas antes, para ofrecerlas al capturar (sin configuración previa). */
export function cuentasUsadas(invoices) {
  const set = new Set();
  for (const inv of invoices || [])
    for (const p of inv.payments || [])
      if (p.cuenta) set.add(p.cuenta);
  return [...set].sort();
}

/**
 * Revierte un pago concreto: registra su opuesto, sin borrar el original.
 *
 * SI EL PAGO TIENE COMPLEMENTO VIVO, NO SE PUEDE. Hay que cancelar el REP
 * primero: si no, quedaría declarado ante el SAT un pago que ya no existe —
 * exactamente lo mismo que motivó la regla de cancelación (`complementosVivos`).
 *
 * La diferencia con aquella, que el operador preguntó y vale la pena dejar
 * escrita: al cancelar una factura muere la FACTURA, y por eso arrastra sus
 * complementos. Al revertir un pago lo que no ocurrió es EL PAGO — el cheque se
 * devolvió— pero la venta sí ocurrió y el vino está en la bodega del cliente.
 * La factura sigue viva y vuelve a estar por cobrar. Decisión del operador:
 * "solo el REP; la factura sigue viva".
 */
export function revertirPago(inv, pagoId) {
  const orig = (inv.payments || []).find(p => p.id === pagoId);
  if (!orig) throw new Error("No se encontró el pago a revertir.");
  if (orig.revierteA) throw new Error("Ese movimiento ya es una reversa.");
  if ((inv.payments || []).some(p => p.revierteA === pagoId))
    throw new Error("Ese pago ya fue revertido.");
  if (complementoVivoDe(inv, pagoId))
    throw new Error("Ese pago tiene un complemento de pago vigente. " +
                    "Hay que cancelar el REP antes de revertirlo: si no, quedaría " +
                    "declarado ante el SAT un pago que ya no existe.");
  return registrarPago(inv, {
    monto: -orig.monto,
    metodo: orig.metodo,
    cuenta: orig.cuenta,
    nota: `Reversa de ${fechaCorta(orig.fecha)} — ${orig.nota || "corrección"}`,
    revierteA: pagoId,
  });
}

/** ¿Este pago ya fue revertido? */
export function estaRevertido(inv, pagoId) {
  return (inv.payments || []).some(p => p.revierteA === pagoId);
}

const fechaCorta = (iso) => {
  try { return new Date(iso).toLocaleDateString("es-MX"); } catch { return iso; }
};

// ═══════════════════════════════════════════════════════════════════════════
// ¿Quién me debe? — la cartera vista por CLIENTE
//
// El operador: "Para las vinícolas falta una vista por clientes. ¿Qué clientes
// tienen facturas o remisiones pendientes de pago?"
//
// Cuentas ya agrupaba por cliente, pero contestaba otra pregunta: ordenaba por
// la última venta y ponía al frente el total histórico. Para saber quién debe
// había que escoger un filtro y leer tarjeta por tarjeta. Esto le da la vuelta:
// solo los que deben, el que más debe primero, y por cada uno cuánto es
// factura y cuánto remisión sin facturar —que se persiguen distinto: la
// factura tiene fecha ante el SAT y, si es PPD, cada cobro pide complemento.
//
// La antigüedad es DESDE LA EMISIÓN. La casa no registra días de crédito por
// cliente (solo PUE/PPD), así que "vencida" no se puede afirmar; "lleva N días
// sin cobrarse" sí, y es lo que se necesita para decidir a quién llamar hoy.
//
// Se parte de las REMISIONES y no de la lista de clientes: un cliente dado de
// baja que todavía debe tiene que seguir apareciendo aquí. El nombre sale del
// catálogo si está y del snapshot si no.
// ═══════════════════════════════════════════════════════════════════════════

const MS_POR_DIA = 86_400_000;

/** Días enteros desde `iso` hasta `hoy`. Nunca negativo. */
export function diasDesde(iso, hoy = new Date()) {
  const t = new Date(iso).getTime();
  const h = new Date(hoy).getTime();
  // Una fecha ilegible no es "cero días": pero tampoco es NaN en pantalla.
  if (Number.isNaN(t) || Number.isNaN(h)) return 0;
  const d = Math.floor((h - t) / MS_POR_DIA);
  return d < 0 ? 0 : d;
}

/**
 * Los clientes que deben, del que más debe al que menos.
 *
 * Cada renglón trae el saldo partido en dos —facturas y remisiones sin
 * facturar—, el documento más antiguo sin cobrar, cuántos complementos faltan
 * y, si lo hay, el saldo a favor de la cuenta (que NO se resta: se aplica o se
 * devuelve, y eso lo decide una persona).
 *
 * `documentos` va del más viejo al más nuevo, que es el orden en que se
 * reclaman.
 */
export function carteraPorCliente(clients = [], invoices = [], { hoy = new Date() } = {}) {
  const catalogo = new Map((clients || []).map(c => [c.id, c]));
  const porCliente = new Map();

  for (const inv of invoices || []) {
    if (remisionCancelada(inv)) continue;
    const saldo = saldoPendiente(inv);
    if (saldo <= 0) continue;
    const id = inv.client?.id;
    if (!id) continue;

    if (!porCliente.has(id)) {
      const c = catalogo.get(id);
      porCliente.set(id, {
        cliente: {
          id,
          name:  c?.name  || inv.client?.name  || "(sin nombre)",
          city:  c?.city  || inv.client?.city  || "",
          state: c?.state || inv.client?.state || "",
          enCatalogo: !!c,
        },
        saldo: 0,
        facturas:   { n: 0, saldo: 0 },
        remisiones: { n: 0, saldo: 0 },
        repPendientes: 0,
        aFavor: Number(c?.aFavor) || 0,
        documentos: [],
      });
    }
    const r = porCliente.get(id);
    const facturada = estadoFacturacion(inv).clave === FACTURACION.facturada.clave;
    const rep = requiereREP(inv);
    r.saldo = redondear(r.saldo + saldo);
    const cubo = facturada ? r.facturas : r.remisiones;
    cubo.n += 1;
    cubo.saldo = redondear(cubo.saldo + saldo);
    if (rep) r.repPendientes += 1;
    r.documentos.push({
      id: inv.id, folio: inv.folio, fecha: inv.date,
      dias: diasDesde(inv.date, hoy),
      total: Number(inv.total) || 0,
      pagado: totalPagado(inv),
      saldo,
      facturada,
      cobranza: estadoCobranza(inv).clave,
      requiereREP: rep,
    });
  }

  const lista = [...porCliente.values()].map(r => {
    r.documentos.sort((a, b) => new Date(a.fecha) - new Date(b.fecha));
    const viejo = r.documentos[0];
    r.masAntigua = viejo ? viejo.fecha : null;
    r.diasMasAntigua = viejo ? viejo.dias : 0;
    return r;
  });

  // El que más debe primero; a igual deuda, el que lleva más tiempo.
  lista.sort((a, b) => (b.saldo - a.saldo) || (b.diasMasAntigua - a.diasMasAntigua));
  return lista;
}

/** Los totales de arriba de la lista: cuánto, de cuántos, y cuánto es factura. */
export function resumenCarteraPorCliente(cartera = []) {
  const r = { clientes: cartera.length, saldo: 0,
              facturas: { n: 0, saldo: 0 }, remisiones: { n: 0, saldo: 0 },
              repPendientes: 0, masAntiguaDias: 0 };
  for (const c of cartera) {
    r.saldo = redondear(r.saldo + c.saldo);
    r.facturas.n += c.facturas.n;
    r.facturas.saldo = redondear(r.facturas.saldo + c.facturas.saldo);
    r.remisiones.n += c.remisiones.n;
    r.remisiones.saldo = redondear(r.remisiones.saldo + c.remisiones.saldo);
    r.repPendientes += c.repPendientes;
    if (c.diasMasAntigua > r.masAntiguaDias) r.masAntiguaDias = c.diasMasAntigua;
  }
  return r;
}

const redondear = (n) => Math.round(n * 100) / 100;
